import type { Todo, User } from "@acto/taskpane/core/domain";

interface TodoBuckets {
  overdue: Todo[];
  open: Todo[];
  completed: Todo[];
}

interface MergedBuckets extends TodoBuckets {
  others: Todo[];
}

// Places a pending row where the refetch will put it (the schema orders `assignedToOthers` by
// creation, newest first). `createdAt` is date-only on the wire, so same-day rows can still shift
// on the post-commit refetch.
function byServerOthersOrder(a: Todo, b: Todo): number {
  const aCreated = a.createdAt ?? "";
  const bCreated = b.createdAt ?? "";
  if (aCreated !== bCreated) return aCreated < bCreated ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/**
 * Moves todos optimistically reassigned to a non-viewer into `others`. Workflow todos never move —
 * the others bucket excludes them server-side.
 */
export function mergeReassignedIntoOthers(
  buckets: TodoBuckets,
  assignedToOthers: Todo[],
  assigneeFor: (todo: Todo) => User | undefined,
  isViewer: (owner: User | null | undefined) => boolean,
): MergedBuckets {
  const moved: Todo[] = [];

  const keep = (todos: Todo[]) =>
    todos.filter((todo) => {
      const effective = assigneeFor(todo);
      const movesToOthers =
        !todo.isWorkflow && effective != null && effective.id !== todo.assignee?.id && !isViewer(effective);
      if (movesToOthers) moved.push(todo.withAssignee(effective));
      return !movesToOthers;
    });

  const overdue = keep(buckets.overdue);
  const open = keep(buckets.open);
  const completed = keep(buckets.completed);

  const others = moved.length === 0 ? assignedToOthers : [...assignedToOthers, ...moved].sort(byServerOthersOrder);
  return { overdue, open, completed, others };
}
